import { AudioSystem } from '../engine/AudioSystem';
import type { Routing } from '../engine/AudioSystem';
import { Meter } from '../engine/protocol';
import { loadProgramData } from '../programs/loader';
import type { ProgramData } from '../programs/schema';
import { KpvStore } from './store';

export const MAX_DECKS = 4;

export type SessionEvent = 'decks' | 'tempo' | 'input';

/**
 * A jam session: several KPV decks over one AudioSystem.
 *
 * - Routing defaults to CHAIN — deck 1 feeds deck 2 feeds … the output, so
 *   two pads stack their effects. PARALLEL runs decks side by side.
 * - One global tempo: BPM/tap fan out to every deck, and new decks join
 *   phase-aligned with deck 1's transport, so loops land in the same pocket.
 */
export class Session {
  readonly system = new AudioSystem();
  decks: KpvStore[] = [];
  focusedIndex = 0;
  routing: Routing = 'chain';
  bpm = 120;
  /** False until someone claims the tempo (tap, manual, MIDI clock). While
   *  false, the FIRST sample recording defines it. */
  tempoSet = false;
  data: ProgramData | null = null;

  private tapTimes: number[] = [];
  private listeners = new Map<SessionEvent, Set<() => void>>();

  on(ev: SessionEvent, fn: () => void): () => void {
    if (!this.listeners.has(ev)) this.listeners.set(ev, new Set());
    this.listeners.get(ev)!.add(fn);
    return () => this.listeners.get(ev)!.delete(fn);
  }

  private emit(ev: SessionEvent): void {
    this.listeners.get(ev)?.forEach((fn) => fn());
  }

  get focused(): KpvStore {
    return this.decks[this.focusedIndex] ?? this.decks[0]!;
  }

  async boot(): Promise<void> {
    await this.system.start();
    this.data = await loadProgramData();
    this.system.onInputChanged = () => this.emit('input');
    this.addDeck();
  }

  addDeck(): KpvStore | null {
    if (this.decks.length >= MAX_DECKS || !this.data) return null;
    const store = new KpvStore(this, this.data);
    store.init();
    store.engine.setBpm(this.bpm);
    // Join in phase with deck 1 so loops line up across decks.
    const masterBeat = this.decks[0]?.engine.meters[Meter.Beat];
    if (masterBeat !== undefined && masterBeat > 0) store.engine.setBeat(masterBeat);
    this.decks.push(store);
    this.focusedIndex = this.decks.length - 1;
    store.selectProgram(this.decks.length === 1 ? 'FLt.1' : 'dLY.1');
    this.rewire();
    this.emit('decks');
    return store;
  }

  removeDeck(index: number): void {
    if (this.decks.length <= 1) return; // always at least one KPV
    const [removed] = this.decks.splice(index, 1);
    removed?.engine.dispose();
    if (this.focusedIndex >= this.decks.length) this.focusedIndex = this.decks.length - 1;
    this.rewire();
    this.emit('decks');
  }

  focus(index: number): void {
    if (index < 0 || index >= this.decks.length || index === this.focusedIndex) return;
    this.focusedIndex = index;
    this.emit('decks');
  }

  focusNext(): void {
    this.focus((this.focusedIndex + 1) % this.decks.length);
  }

  setRouting(routing: Routing): void {
    if (routing === this.routing) return;
    this.routing = routing;
    this.rewire();
    this.emit('decks');
  }

  private rewire(): void {
    this.system.wire(this.decks.map((d) => d.engine.node), this.routing);
  }

  // --- Global tempo -----------------------------------------------------------

  setBpm(bpm: number): void {
    this.tempoSet = true;
    this.applyBpm(bpm);
  }

  /** Tempo derived from a recording (does not require prior tempoSet). */
  adoptTempo(bpm: number): void {
    this.tempoSet = true;
    this.applyBpm(bpm);
  }

  private applyBpm(bpm: number): void {
    this.bpm = Math.min(300, Math.max(20, Math.round(bpm * 10) / 10));
    for (const d of this.decks) d.engine.setBpm(this.bpm);
    this.emit('tempo');
  }

  tapTempo(): void {
    const now = performance.now();
    if (this.tapTimes.length && now - this.tapTimes[this.tapTimes.length - 1]! > 2000) {
      this.tapTimes = [];
    }
    this.tapTimes.push(now);
    if (this.tapTimes.length >= 2) {
      const recent = this.tapTimes.slice(-5);
      const intervals: number[] = [];
      for (let i = 1; i < recent.length; i++) intervals.push(recent[i]! - recent[i - 1]!);
      this.setBpm(60000 / (intervals.reduce((a, b) => a + b, 0) / intervals.length));
    }
  }

  private inputDeviceIdx = -1;

  /**
   * First call: connect the default input (this also unlocks device labels).
   * Subsequent calls: cycle through available input devices.
   */
  async connectInput(): Promise<void> {
    try {
      if (this.inputDeviceIdx < 0) {
        await this.system.enableInput();
        this.inputDeviceIdx = 0;
        return;
      }
      const devices = await AudioSystem.listInputDevices();
      if (devices.length === 0) return;
      this.inputDeviceIdx = (this.inputDeviceIdx + 1) % devices.length;
      await this.system.enableInput(devices[this.inputDeviceIdx]!.deviceId);
    } catch (err) {
      console.warn('input unavailable:', err);
      this.emit('input');
    }
  }
}
